
import Vue from 'vue';
import Component from "vue-class-component";
import HeadingWave from "@/features/shared/components/HeadingWave.vue";
import {
    AddOrUpdateShipTypeRequest,
    CountriesClient,
    CountryLightModel,
    factory, ResultError, ShipTypeDetailsModel,
    SysAdminShipTypesClient,
} from "@/api-client";
import toasts from "@/components/Toasts";
import LabelOptional from "@/features/shared/components/LabelOptional.vue";
import AdministrationDetailviewLayout from "@/features/administration/AdministrationDetailviewLayout.vue";
import {Prop} from "vue-property-decorator";
import {NavigationGuardNext} from "vue-router/types/router";
import ErrorsSummary from "@/components/ErrorsSummary.vue";

interface Props {
    model?: AddOrUpdateShipTypeRequest;
    isActive?: boolean;
}

@Component({
    components: {
        ErrorsSummary,
        HeadingWave,
        LabelOptional,
        AdministrationDetailviewLayout
    },
    beforeRouteEnter(to, from, next: NavigationGuardNext<UpsertShipType>) {
        const props = to.meta!.props as Props;
        Promise.all([
            to.params.id ? new SysAdminShipTypesClient().details(to.params.id) : Promise.resolve(null)
        ]).then(values => {
            const shipTypeToUpdate: ShipTypeDetailsModel | null = values[0];
            if (shipTypeToUpdate) {
                props.model = new AddOrUpdateShipTypeRequest({
                    name: shipTypeToUpdate.name.estonian,
                    nameEn: shipTypeToUpdate.name.english
                });
                props.isActive = shipTypeToUpdate.isActive;
            }
            next();
        }).catch(e => next(e))
    }
})
export default class UpsertShipType extends Vue implements Props {
    @Prop({required: false, default: () => factory.create(AddOrUpdateShipTypeRequest)})
    model!: AddOrUpdateShipTypeRequest;

    @Prop({required: false, default: () => true})
    isActive!: boolean;

    validationErrors: ResultError = ResultError.createEmpty();

    get isEditMode(): boolean {
        return this.$route.params.id != null;
    }

    get isActiveLabel(): string {
        return `${this.$ls.Administration_ShipTypes_CurrentShipTypeActivity} ${this.isActive ? this.$ls.Global_Label_Active : this.$ls.Global_Label_InActive}`
    }

    get activationButtonText(): string {
        return this.isActive ? this.$ls.Administration_ShipTypes_Deactivate_Action : this.$ls.Administration_ShipTypes_Activate_Action;
    }

    get id(): string | null {
        return this.$route.params.id;
    }

    async save() {
        this.validationErrors.clear();

        if (this.isEditMode) {
            await new SysAdminShipTypesClient().update(this.$route.params.id, this.model)
                .then(
                    () => {
                        this.$router.push({name: 'ship-types'});
                        toasts.success(`${this.$ls.Administration_ShipTypes_Toasts_Update_Success}: ${this.model.name}`);
                    },
                    (errorResponse: ResultError) => {
                        this.validationErrors = errorResponse;
                        toasts.failure(this.$ls.Global_Toast_ActionFailureWithErrorsAbove);
                    });
        } else {
            await new SysAdminShipTypesClient().add(this.model)
                .then(
                    () => {
                        this.$router.push({name: 'ship-types'});
                        toasts.success(`${this.$ls.Administration_ShipTypes_Toasts_Adding_Success}: ${this.model.name}`);
                    },
                    (errorResponse: ResultError) => {
                        this.validationErrors = errorResponse;
                        toasts.failure(this.$ls.Global_Toast_ActionFailureWithErrorsAbove);
                    });
        }
    }

    async changeActiveState() {
        this.validationErrors.clear();

        if (this.isActive) {
            await new SysAdminShipTypesClient().deactivate(this.$route.params.id)
                .then(
                    () => {
                        this.$router.push({name: 'ship-types'});
                        toasts.success(`${this.$ls.Administration_ShipTypes_Toasts_Deactivation_Success}: ${this.model.name}`);
                    },
                    (errorResponse: ResultError) => {
                        this.validationErrors = errorResponse;
                        toasts.failure(this.$ls.Global_Toast_ActionFailureWithErrorsAbove);
                    });
        } else {
            await new SysAdminShipTypesClient().activate(this.$route.params.id)
                .then(
                    () => {
                        this.$router.push({name: 'ship-types'});
                        toasts.success(`${this.$ls.Administration_ShipTypes_Toasts_Activation_Success}: ${this.model.name}`);
                    },
                    (errorResponse: ResultError) => {
                        this.validationErrors = errorResponse;
                        toasts.failure(this.$ls.Global_Toast_ActionFailureWithErrorsAbove);
                    });
        }
    }
}
